import React from 'react';
import { useSelector } from 'react-redux';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Separator,
  Button,
} from '@lb-classroom/ui';
import ModalMessage from '../../UI/organisms/ModalMessage';
import classnames from 'classnames';
import LoadingMessage from '../../UI/organisms/AppLoadingMessage';
import MomentFormatter from '../../../utils/MomentFormatter';
import priceHelperUtils from '../../../utils/priceHelperUtils';
import moneyUtils from '../../../utils/moneyUtils';
import {
  useUnpaidInvoiceQuery,
  usePayInvoiceMutation,
} from '../../../hooks/queries';
import { i18n, withTranslation } from 'next-i18next';
import appUtils from '../../../utils/appUtils';
import { useFlashMessage } from '../../../hooks/use-flash-message';
import { X } from 'lucide-react';

interface PayInvoiceModalContentProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  message: string;
  messageType: string;
  hideModal: () => void;
}

const PayInvoiceModalContent = (props: PayInvoiceModalContentProps) => {
  const { hideModal, t, message, messageType } = props;
  const { addFlash } = useFlashMessage();

  // Get geoData and currentUser from Redux (not migrated yet)
  const geoData = useSelector((state: any) => state.geoData?.geoData);
  const currentUser = useSelector((state: any) => state.auth?.user);

  // Use TanStack Query for billing history data
  const { data: unpaidInvoice, isLoading: loading } = useUnpaidInvoiceQuery();
  const payMutation = usePayInvoiceMutation();

  const paying = payMutation.isPending;

  const handleSubmit = async () => {
    if (unpaidInvoice) {
      try {
        const result = await payMutation.mutateAsync(unpaidInvoice.id);
        hideModal();
        addFlash(result.message, {
          type: 'success',
          autoDisappear: true,
          sticky: true,
        });
      } catch (error: unknown) {
        hideModal();
        const errorMessage =
          error instanceof Error ? error.message : 'An error occurred';
        addFlash(errorMessage, { type: 'error', autoDisappear: false });
      }
    }
  };

  const handleHideModal = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!paying) hideModal();
  };

  const timeZone = currentUser?.js_time_zone || geoData?.timeZone;
  const currentLocale = appUtils.getLocaleFromLang(i18n?.language || 'en');
  const momentFormatter = new MomentFormatter(currentLocale, timeZone);
  let amount = 0;
  let periodSentence = 'Loading..';
  let currencyCode;
  if (unpaidInvoice) {
    amount = priceHelperUtils.getPriceInDollars(
      unpaidInvoice.currency,
      (unpaidInvoice as any).total_amount,
    );
    currencyCode = moneyUtils.currencyToCode(unpaidInvoice.currency);
    const periodStart = (unpaidInvoice as any).period_start;
    const periodEnd = (unpaidInvoice as any).period_end;
    periodSentence =
      momentFormatter.invoiceDateFormat(periodStart) +
      ' - ' +
      momentFormatter.invoiceDateFormat(periodEnd);
  }
  const wrapperClass = classnames('modal-loading-message', {
    loading: paying || loading,
  });
  const loadingMessage = paying ? 'Processing...' : null;
  const loadingClass = 'modal-loading-message';

  return (
    <div className={wrapperClass}>
      <Card className="z-50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>{t('student.billing.payInvoice.title')}</CardTitle>
          <button type="button" className="modal-close" onClick={handleHideModal}>
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </CardHeader>
        <CardContent>
          <LoadingMessage
            message={loadingMessage}
            loadingClass={loadingClass}
          />
          <ModalMessage message={message} messageType={messageType} />
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 sm:col-span-10">
              <div className="grid grid-cols-12 gap-4">
                <div className="checkout-review-plan-name col-span-12">
                  <div className="float-left">
                    <strong>{t('student.billing.payInvoice.period')}</strong>
                  </div>
                  <div className="float-right">{periodSentence}</div>
                </div>
              </div>
              <Separator className="my-4" />
              <div className="grid grid-cols-12 gap-4">
                <div className="checkout-review-plan-name col-span-12">
                  <div className="float-left">
                    <strong>{t('student.billing.payInvoice.overdue')}</strong>
                  </div>
                  <div className="float-right">
                    {currencyCode}
                    {amount}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Separator className="my-4" />
          <div className="control-group text-right">
            <Button
              variant="outline"
              disabled={paying}
              onClick={handleHideModal}
            >
              {t('links.cancel')}
            </Button>
            <Button onClick={handleSubmit} type="submit" disabled={paying}>
              {t('links.pay')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const wrapped = withTranslation('common')(PayInvoiceModalContent);

export default wrapped;
